import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { List, X } from 'lucide-react';
import { chapters } from '../data/horizon';
import { scrollTo } from '../utils/scroll';
import { useActiveChapter } from '../hooks/useActiveChapter';
const EASE = [0.23, 1, 0.32, 1] as const;
const RING = 2 * Math.PI * 17;
export function MobileIndex() {
  const [open, setOpen] = useState(false);
  const active = useActiveChapter();
  const reduceMotion = useReducedMotion();
  const activeIndex = Math.max(0, chapters.findIndex(c => c.id === active));
  const progress = (activeIndex + 1) / chapters.length;
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const d = (s: number) => reduceMotion ? 0 : s;
  return createPortal(<>
      <motion.button type="button" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-label={`Capítulos — estás en ${activeIndex + 1} de ${chapters.length}`} initial={{
      opacity: 0,
      y: 12,
      scale: 0.96
    }} animate={{
      opacity: open ? 0 : 1,
      y: 0,
      scale: open ? 0.96 : 1
    }} transition={{
      duration: d(0.22),
      ease: EASE
    }} whileTap={{
      scale: 0.95
    }} className="fixed bottom-5 left-4 z-50 flex h-12 items-center gap-2.5 rounded-full border border-[#D4AF37]/40 bg-[#0A1628]/90 pl-1.5 pr-4 shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-xl md:bottom-6 md:left-6" style={{
      pointerEvents: open ? 'none' : 'auto'
    }} data-id="element-1145" data-mp="3m0dbx7nacg02">
        <span className="relative flex h-9 w-9 items-center justify-center" data-id="element-1146" data-mp="2fgm44j9k6hag">
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 36 36" aria-hidden="true" data-id="element-1147" data-mp="2kdmcj0tvrw4q">
            <circle cx="18" cy="18" r="17" fill="none" stroke="rgba(212,175,55,0.15)" strokeWidth="2" data-id="element-1148" data-mp="2cd6h6bivf5cy" />
            <motion.circle cx="18" cy="18" r="17" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeDasharray={RING} animate={{
            strokeDashoffset: RING * (1 - progress)
          }} transition={{
            duration: d(0.3),
            ease: EASE
          }} data-id="element-1149" data-mp="27fefwqwc66w3" />
          </svg>
          <List className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-1150" data-mp="2qcfoelp3mp4s" />
        </span>
        <span className="flex flex-col items-start leading-tight" data-id="element-1151" data-mp="2qad0c77ib62p">
          <span className="font-cinzel text-[13px] font-bold text-white" data-id="element-1152" data-mp="3500soixpmuea">Capítulos</span>
          <span className="text-[11px] text-gray-400 tabular-nums" data-id="element-1153" data-mp="303ac7uhztwsc">
            {String(activeIndex + 1).padStart(2, '0')} · {chapters[activeIndex].short}
          </span>
        </span>
      </motion.button>

      <AnimatePresence data-id="element-1154" data-mp="3a1q1rt1dgxzq">
        {open && <>
            <motion.div key="backdrop" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: d(0.2)
        }} className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden="true" data-id="element-1155" data-mp="3soyzl1rxoktu" />
            <motion.div key="sheet" role="dialog" aria-modal="true" aria-label="Capítulos de la guía" initial={{
          opacity: 0,
          y: 24,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 24,
          scale: 0.98
        }} transition={{
          duration: d(0.26),
          ease: EASE
        }} className="fixed inset-x-0 bottom-0 z-[71] max-h-[80vh] overflow-y-auto rounded-t-3xl border-t border-[#D4AF37]/30 bg-[#0A1628] px-4 pb-8 pt-4 md:inset-x-auto md:left-6 md:bottom-6 md:w-80 md:rounded-3xl md:border md:pb-4" data-id="element-1156" data-mp="39y7sy7zbz8j5">
              <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/20 md:hidden" aria-hidden="true" data-id="element-1157" data-mp="25f5qw3tdgu8" />
              <div className="mb-2 flex items-center justify-between px-2" data-id="element-1158" data-mp="3ubkfd02dqbua">
                <p className="font-cinzel text-base font-bold text-white" data-id="element-1159" data-mp="3piramc6wv3i7">Capítulos</p>
                <button type="button" onClick={() => setOpen(false)} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-gray-400 transition-colors duration-150 hover:text-white" aria-label="Cerrar" data-id="element-1160" data-mp="3kkpuo04s7kay">
                  <X className="h-4 w-4" data-id="element-1161" data-mp="3alifd7ehlfl7" />
                </button>
              </div>
              <ol className="space-y-0.5" data-id="element-1162" data-mp="36jwxf7n3ib45">
                {chapters.map((c, i) => {
              const Icon = c.icon;
              const isActive = c.id === active;
              return <motion.li key={c.id} initial={{
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: d(0.2),
                delay: d(Math.min(i * 0.025, 0.25)),
                ease: EASE
              }} data-id="element-1163" data-mp="14sadtr5j9ihp" data-mpi={i}>
                      <button type="button" onClick={() => {
                  setOpen(false);
                  setTimeout(() => scrollTo(c.id), 60);
                }} aria-current={isActive ? 'true' : undefined} className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors duration-150 ${isActive ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'text-gray-200 hover:bg-white/5'}`} data-id="element-1164" data-mp="1cbt1iapq1zwm">
                        <span className="w-6 font-cinzel text-xs text-[#D4AF37]/60 tabular-nums" data-id="element-1165" data-mp="ycjwm76yus7y">{String(i + 1).padStart(2, '0')}</span>
                        <Icon className="h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-1166" data-mp="12g864vf5rxvu" />
                        <span data-id="element-1167" data-mp="29rgqpvi0c7m6">{c.short}</span>
                      </button>
                    </motion.li>;
            })}
              </ol>
            </motion.div>
          </>}
      </AnimatePresence>
    </>, document.body);
}